<script setup lang="ts">
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import { Badge } from "@/components/ui/badge";

import {
  Brain,
  Cpu,
  Network,
  Shield,
  Zap,
  BarChart3,
  Cloud,
  Cog,
} from "lucide-vue-next";

interface TechAdvantageProps {
  icon: string;
  title: string;
  description: string;
  technologies: string[];
  level: string;
}

const techAdvantagesList: TechAdvantageProps[] = [
  {
    icon: "Brain",
    title: "人工智能算法",
    description: "先进的机器学习和深度学习算法，实现智能预测和决策支持。",
    technologies: ["TensorFlow", "PyTorch", "深度学习", "机器学习"],
    level: "核心技术",
  },
  {
    icon: "Network",
    title: "物联网架构",
    description: "完整的物联网生态系统，支持多种通信协议和设备接入。",
    technologies: ["LoRa", "NB-IoT", "WiFi", "蓝牙"],
    level: "核心技术",
  },
  {
    icon: "Cpu",
    title: "边缘计算",
    description: "本地化数据处理能力，降低延迟，提升系统响应速度。",
    technologies: ["边缘AI", "实时处理", "本地存储", "智能网关"],
    level: "优势技术",
  },
  {
    icon: "Cloud",
    title: "云端集成",
    description: "混合云架构，实现本地与云端的无缝协同和数据同步。",
    technologies: ["微服务", "容器化", "API网关", "数据同步"],
    level: "优势技术",
  },
  {
    icon: "BarChart3",
    title: "数据分析",
    description: "专业的大数据分析平台，提供实时监控和智能报表功能。",
    technologies: ["大数据", "实时分析", "可视化", "商业智能"],
    level: "专业服务",
  },
  {
    icon: "Shield",
    title: "安全防护",
    description: "企业级安全体系，多重加密保障，符合国家信息安全标准。",
    technologies: ["数据加密", "访问控制", "安全审计", "威胁检测"],
    level: "专业服务",
  },
  {
    icon: "Zap",
    title: "自动化控制",
    description: "工业4.0自动化解决方案，PLC编程和SCADA系统集成。",
    technologies: ["PLC", "SCADA", "HMI", "工业通信"],
    level: "专业服务",
  },
  {
    icon: "Cog",
    title: "系统集成",
    description: "完整的系统集成服务，从硬件部署到软件配置的一站式解决方案。",
    technologies: ["硬件集成", "软件配置", "系统调试", "运维支持"],
    level: "专业服务",
  },
];

const iconMap: Record<string, any> = {
  Brain,
  Network,
  Cpu,
  Cloud,
  BarChart3,
  Shield,
  Zap,
  Cog,
};

const getLevelColor = (level: string) => {
  switch (level) {
    case "核心技术":
      return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300";
    case "优势技术":
      return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300";
    case "专业服务":
      return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
    default:
      return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
  }
};
</script>

<template>
  <section
    id="team"
    class="container lg:w-[75%] py-24 sm:py-32"
  >
    <div class="text-center mb-8">
      <h2 class="text-lg text-primary text-center mb-2 tracking-wider">技术优势</h2>

      <h2 class="text-3xl md:text-4xl text-center font-bold">
        核心技术实力 · 创新驱动发展
      </h2>
    </div>

    <div
      class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8"
    >
      <Card
        v-for="{
          icon,
          title,
          description,
          technologies,
          level,
        } in techAdvantagesList"
        :key="title"
        class="bg-muted/60 dark:bg-card flex flex-col h-full overflow-hidden group hover:shadow-lg transition-all duration-300"
      >
        <CardHeader class="p-6 pb-4">
          <div class="flex items-center justify-between mb-4">
            <div class="p-3 rounded-lg bg-primary/10">
              <component 
                :is="iconMap[icon]" 
                class="h-6 w-6 text-primary"
              />
            </div>
            <Badge :class="getLevelColor(level)" class="text-xs">
              {{ level }}
            </Badge>
          </div>
          <CardTitle class="text-lg font-semibold">
            {{ title }}
          </CardTitle>
        </CardHeader>

        <CardContent class="px-6 pb-4 flex-1">
          <p class="text-muted-foreground text-sm mb-4">
            {{ description }}
          </p>
          <div class="flex flex-wrap gap-2">
            <Badge
              v-for="tech in technologies"
              :key="tech"
              variant="secondary"
              class="text-xs"
            >
              {{ tech }}
            </Badge>
          </div>
        </CardContent>
      </Card>
    </div>
  </section>
</template>
